import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';

interface IToggleRowProps {
  label: React.ReactNode;
  description?: React.ReactNode;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * A single setting row: label + description on the left, Switch on the right.
 * Designed to sit inside a `divide-y` list so rows read as a calm settings list.
 * Shared across the bot config surfaces (Settings, Video Avatar, …).
 */
const ToggleRow: React.FC<IToggleRowProps> = ({
  label, description, checked, onCheckedChange, disabled, className,
}) => (
  <div className={cn('flex items-start justify-between gap-4 py-4', className)}>
    <div className="min-w-0">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      {description && <p className="mt-0.5 text-[13px] leading-relaxed text-gray-500">{description}</p>}
    </div>
    <Switch
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      // The label is a sibling paragraph, not a <label for>, so without this a
      // screen reader reads every settings row as an unnamed switch.
      aria-label={typeof label === 'string' ? label : undefined}
      className="mt-0.5 shrink-0"
    />
  </div>
);

export default ToggleRow;
